import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { keys } from './keys';
import { invalidate, type QueryOptions } from './query-client';
import { request } from './client';
import type {
  CampaignPreview,
  CampaignSegment,
  CampaignStatus,
  ConsentCategory,
  ConsentEvent,
  ConsentInput,
  ConsentVerdict,
  ContactStatus,
  CreateTemplateInput,
  ImportResult,
  ListQuery,
  Paginated,
  TemplateCategory,
  TemplateStatus,
  WaScope,
  WhatsappAccount,
  WhatsappAnalytics,
  WhatsappCampaign,
  WhatsappConfig,
  WhatsappContact,
  WhatsappEvent,
  WhatsappMessage,
  WhatsappTemplate,
  WhatsappUsage,
} from './types';

/** /whatsapp/*: the WhatsApp hub. */
export const whatsappApi = {
  config: (scope: WaScope = {}) => request<WhatsappConfig>('/whatsapp/config', { query: scope }),
  accounts: () => request<WhatsappAccount[]>('/whatsapp/accounts'),
  connect: (
    body: {
      code?: string;
      accessToken?: string;
      wabaId: string;
      phoneNumberId: string;
      pin?: string;
    },
    scope: WaScope = {},
  ) => request<WhatsappAccount>('/whatsapp/connect', { method: 'POST', body, query: scope }),
  sync: (scope: WaScope = {}) =>
    request<WhatsappAccount>('/whatsapp/account/sync', { method: 'POST', query: scope }),
  disconnect: (scope: WaScope = {}) =>
    request<void>('/whatsapp/account', { method: 'DELETE', query: scope }),
  rotateOptinToken: (scope: WaScope = {}) =>
    request<{ token: string }>('/whatsapp/account/optin-token', { method: 'POST', query: scope }),
  setShopify: (
    body: { shopDomain: string; webhookSecret: string; optinTag?: string },
    scope: WaScope = {},
  ) =>
    request<WhatsappAccount>('/whatsapp/account/shopify', { method: 'POST', body, query: scope }),

  contacts: (
    query: ListQuery & { status?: ContactStatus; q?: string; consentedTo?: ConsentCategory } = {},
    scope: WaScope = {},
  ) => request<Paginated<WhatsappContact>>('/whatsapp/contacts', { query: { ...query, ...scope } }),
  contact: (id: string, scope: WaScope = {}) =>
    request<WhatsappContact>(`/whatsapp/contacts/${id}`, { query: scope }),
  consentTrail: (id: string, scope: WaScope = {}) =>
    request<ConsentEvent[]>(`/whatsapp/contacts/${id}/consent`, { query: scope }),
  importContacts: (
    body: {
      rows: Array<{ phone: string; name?: string; email?: string }>;
      consent: ConsentInput;
      defaultCountryCode?: string;
    },
    scope: WaScope = {},
  ) => request<ImportResult>('/whatsapp/contacts/import', { method: 'POST', body, query: scope }),
  optIn: (
    body: {
      phone: string;
      name?: string;
      email?: string;
      consent: ConsentInput;
      defaultCountryCode?: string;
    },
    scope: WaScope = {},
  ) => request<WhatsappContact>('/whatsapp/optin', { method: 'POST', body, query: scope }),
  optOut: (id: string, body: { reason?: string } = {}, scope: WaScope = {}) =>
    request<WhatsappContact>(`/whatsapp/contacts/${id}/optout`, {
      method: 'POST',
      body,
      query: scope,
    }),
  block: (id: string, scope: WaScope = {}) =>
    request<WhatsappContact>(`/whatsapp/contacts/${id}/block`, { method: 'POST', query: scope }),
  erase: (id: string, scope: WaScope = {}) =>
    request<void>(`/whatsapp/contacts/${id}`, { method: 'DELETE', query: scope }),
  consentCheck: (
    body: { contactId?: string; phone?: string; category: TemplateCategory; templateId?: string },
    scope: WaScope = {},
  ) => request<ConsentVerdict>('/whatsapp/consent/check', { method: 'POST', body, query: scope }),

  templates: (
    query: { status?: TemplateStatus; category?: TemplateCategory } = {},
    scope: WaScope = {},
  ) => request<WhatsappTemplate[]>('/whatsapp/templates', { query: { ...query, ...scope } }),
  createTemplate: (body: CreateTemplateInput, scope: WaScope = {}) =>
    request<WhatsappTemplate>('/whatsapp/templates', { method: 'POST', body, query: scope }),
  syncTemplates: (scope: WaScope = {}) =>
    request<{ synced: number; inserted: number }>('/whatsapp/templates/sync', {
      method: 'POST',
      query: scope,
    }),
  deleteTemplate: (id: string, scope: WaScope = {}) =>
    request<void>(`/whatsapp/templates/${id}`, { method: 'DELETE', query: scope }),

  campaigns: (query: ListQuery & { status?: CampaignStatus } = {}, scope: WaScope = {}) =>
    request<Paginated<WhatsappCampaign>>('/whatsapp/campaigns', { query: { ...query, ...scope } }),
  campaign: (id: string, scope: WaScope = {}) =>
    request<WhatsappCampaign>(`/whatsapp/campaigns/${id}`, { query: scope }),
  createCampaign: (
    body: {
      name: string;
      templateId: string;
      mediaUrl?: string;
      variables?: string[];
      segment?: CampaignSegment;
      scheduledAt?: string;
    },
    scope: WaScope = {},
  ) => request<WhatsappCampaign>('/whatsapp/campaigns', { method: 'POST', body, query: scope }),
  previewCampaign: (id: string, scope: WaScope = {}) =>
    request<CampaignPreview>(`/whatsapp/campaigns/${id}/preview`, { method: 'POST', query: scope }),
  sendCampaign: (id: string, body: { scheduledAt?: string } = {}, scope: WaScope = {}) =>
    request<WhatsappCampaign>(`/whatsapp/campaigns/${id}/send`, {
      method: 'POST',
      body,
      query: scope,
    }),
  pauseCampaign: (id: string, scope: WaScope = {}) =>
    request<WhatsappCampaign>(`/whatsapp/campaigns/${id}/pause`, { method: 'POST', query: scope }),
  resumeCampaign: (id: string, scope: WaScope = {}) =>
    request<WhatsappCampaign>(`/whatsapp/campaigns/${id}/resume`, { method: 'POST', query: scope }),
  cancelCampaign: (id: string, scope: WaScope = {}) =>
    request<WhatsappCampaign>(`/whatsapp/campaigns/${id}/cancel`, { method: 'POST', query: scope }),
  sendMessage: (
    body: {
      contactId?: string;
      phone?: string;
      templateId: string;
      variables?: string[];
      mediaUrl?: string;
      reference?: string;
    },
    scope: WaScope = {},
  ) => request<WhatsappMessage>('/whatsapp/messages', { method: 'POST', body, query: scope }),

  analytics: (
    query: { campaignId?: string; from?: string; to?: string } = {},
    scope: WaScope = {},
  ) => request<WhatsappAnalytics>('/whatsapp/analytics', { query: { ...query, ...scope } }),
  usage: (query: { month?: string } = {}, scope: WaScope = {}) =>
    request<WhatsappUsage>('/whatsapp/usage', { query: { ...query, ...scope } }),
  events: (
    query: ListQuery & { eventType?: string; campaignId?: string } = {},
    scope: WaScope = {},
  ) => request<Paginated<WhatsappEvent>>('/whatsapp/events', { query: { ...query, ...scope } }),
};

type ContactsQuery = NonNullable<Parameters<typeof whatsappApi.contacts>[0]>;
type TemplatesQuery = NonNullable<Parameters<typeof whatsappApi.templates>[0]>;
type CampaignsQuery = NonNullable<Parameters<typeof whatsappApi.campaigns>[0]>;
type AnalyticsQuery = NonNullable<Parameters<typeof whatsappApi.analytics>[0]>;
type UsageQuery = NonNullable<Parameters<typeof whatsappApi.usage>[0]>;
type EventsQuery = NonNullable<Parameters<typeof whatsappApi.events>[0]>;
type ConnectBody = Parameters<typeof whatsappApi.connect>[0];
type ShopifyBody = Parameters<typeof whatsappApi.setShopify>[0];
type ImportBody = Parameters<typeof whatsappApi.importContacts>[0];
type OptInBody = Parameters<typeof whatsappApi.optIn>[0];
type OptOutBody = Parameters<typeof whatsappApi.optOut>[1];
type ConsentCheckBody = Parameters<typeof whatsappApi.consentCheck>[0];
type CreateCampaignBody = Parameters<typeof whatsappApi.createCampaign>[0];
type SendCampaignBody = Parameters<typeof whatsappApi.sendCampaign>[1];
type SendMessageBody = Parameters<typeof whatsappApi.sendMessage>[0];

/* ---- reads: one cache segment per scope (see keys.whatsapp) ------------- */

export function useWaConfig(scope: WaScope = {}, options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.whatsapp.config(scope),
    queryFn: () => whatsappApi.config(scope),
    ...options,
  });
}

/** Staff: every connected account across tenants. */
export function useWaAccounts(options: QueryOptions = {}) {
  return useQuery({
    queryKey: keys.whatsapp.accounts,
    queryFn: () => whatsappApi.accounts(),
    ...options,
  });
}

export function useWaContacts(
  query: ContactsQuery = {},
  scope: WaScope = {},
  options: QueryOptions = {},
) {
  return useQuery({
    queryKey: keys.whatsapp.contacts(scope, query),
    queryFn: () => whatsappApi.contacts(query, scope),
    placeholderData: keepPreviousData,
    ...options,
  });
}

export function useWaContact(id: string | null, scope: WaScope = {}) {
  return useQuery({
    queryKey: keys.whatsapp.contact(scope, id),
    queryFn: id ? () => whatsappApi.contact(id, scope) : skipToken,
  });
}

export function useWaConsentTrail(id: string | null, scope: WaScope = {}) {
  return useQuery({
    queryKey: keys.whatsapp.consentTrail(scope, id),
    queryFn: id ? () => whatsappApi.consentTrail(id, scope) : skipToken,
  });
}

export function useWaTemplates(
  query: TemplatesQuery = {},
  scope: WaScope = {},
  options: QueryOptions = {},
) {
  return useQuery({
    queryKey: keys.whatsapp.templates(scope, query),
    queryFn: () => whatsappApi.templates(query, scope),
    placeholderData: keepPreviousData,
    ...options,
  });
}

export function useWaCampaigns(
  query: CampaignsQuery = {},
  scope: WaScope = {},
  options: QueryOptions = {},
) {
  return useQuery({
    queryKey: keys.whatsapp.campaigns(scope, query),
    queryFn: () => whatsappApi.campaigns(query, scope),
    placeholderData: keepPreviousData,
    ...options,
  });
}

export function useWaCampaign(id: string | null, scope: WaScope = {}) {
  return useQuery({
    queryKey: keys.whatsapp.campaign(scope, id),
    queryFn: id ? () => whatsappApi.campaign(id, scope) : skipToken,
  });
}

export function useWaAnalytics(
  query: AnalyticsQuery = {},
  scope: WaScope = {},
  options: QueryOptions = {},
) {
  return useQuery({
    queryKey: keys.whatsapp.analytics(scope, query),
    queryFn: () => whatsappApi.analytics(query, scope),
    placeholderData: keepPreviousData,
    ...options,
  });
}

export function useWaUsage(
  query: UsageQuery = {},
  scope: WaScope = {},
  options: QueryOptions = {},
) {
  return useQuery({
    queryKey: keys.whatsapp.usage(scope, query),
    queryFn: () => whatsappApi.usage(query, scope),
    placeholderData: keepPreviousData,
    ...options,
  });
}

export function useWaEvents(
  query: EventsQuery = {},
  scope: WaScope = {},
  options: QueryOptions = {},
) {
  return useQuery({
    queryKey: keys.whatsapp.events(scope, query),
    queryFn: () => whatsappApi.events(query, scope),
    placeholderData: keepPreviousData,
    ...options,
  });
}

/* ---- writes ------------------------------------------------------------- */

/**
 * A write inside one scope: every read cached for that scope (config, contacts,
 * templates, campaigns, analytics) is refetched afterwards. Account-level writes
 * also refresh the staff account list.
 */
function useScopedMutation<TVars = void, TData = unknown>(
  scope: WaScope,
  mutationFn: (vars: TVars) => Promise<TData>,
  account = false,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () =>
      invalidate(
        queryClient,
        keys.whatsapp.scope(scope),
        ...(account ? [keys.whatsapp.accounts] : []),
      ),
  });
}

export function useWaConnect(scope: WaScope = {}) {
  return useScopedMutation(scope, (body: ConnectBody) => whatsappApi.connect(body, scope), true);
}

export function useWaSync(scope: WaScope = {}) {
  return useScopedMutation(scope, () => whatsappApi.sync(scope), true);
}

export function useWaDisconnect(scope: WaScope = {}) {
  return useScopedMutation(scope, () => whatsappApi.disconnect(scope), true);
}

export function useWaRotateOptinToken(scope: WaScope = {}) {
  return useScopedMutation(scope, () => whatsappApi.rotateOptinToken(scope), true);
}

export function useWaSetShopify(scope: WaScope = {}) {
  return useScopedMutation(scope, (body: ShopifyBody) => whatsappApi.setShopify(body, scope), true);
}

/** Rows per request when importing a file. */
export const IMPORT_CHUNK = 500;

type ImportVars = ImportBody & { onProgress?: (percent: number) => void };

/**
 * Imports a whole file, IMPORT_CHUNK rows per request, and sums the results;
 * skipped rows keep their row number in the file. One invalidation at the end.
 */
async function importInChunks({ rows, onProgress, ...rest }: ImportVars, scope: WaScope) {
  const totals: ImportResult = { received: 0, created: 0, updated: 0, subscribed: 0, skipped: [] };
  for (let i = 0; i < rows.length; i += IMPORT_CHUNK) {
    onProgress?.(Math.round((i / rows.length) * 100));
    const result = await whatsappApi.importContacts(
      { ...rest, rows: rows.slice(i, i + IMPORT_CHUNK) },
      scope,
    );
    totals.received += result.received;
    totals.created += result.created;
    totals.updated += result.updated;
    totals.subscribed += result.subscribed;
    totals.skipped.push(...result.skipped.map((row) => ({ ...row, row: row.row + i })));
  }
  return totals;
}

export function useWaImportContacts(scope: WaScope = {}) {
  return useScopedMutation(scope, (vars: ImportVars) => importInChunks(vars, scope));
}

export function useWaOptIn(scope: WaScope = {}) {
  return useScopedMutation(scope, (body: OptInBody) => whatsappApi.optIn(body, scope));
}

export function useWaOptOut(scope: WaScope = {}) {
  return useScopedMutation(scope, ({ id, body }: { id: string; body?: OptOutBody }) =>
    whatsappApi.optOut(id, body, scope),
  );
}

export function useWaBlockContact(scope: WaScope = {}) {
  return useScopedMutation(scope, (id: string) => whatsappApi.block(id, scope));
}

export function useWaEraseContact(scope: WaScope = {}) {
  return useScopedMutation(scope, (id: string) => whatsappApi.erase(id, scope));
}

/** A dry run (POST, nothing stored): no cache changes, but a user action, so a mutation. */
export function useWaConsentCheck(scope: WaScope = {}) {
  return useMutation({
    mutationFn: (body: ConsentCheckBody) => whatsappApi.consentCheck(body, scope),
  });
}

export function useWaCreateTemplate(scope: WaScope = {}) {
  return useScopedMutation(scope, (body: CreateTemplateInput) =>
    whatsappApi.createTemplate(body, scope),
  );
}

export function useWaSyncTemplates(scope: WaScope = {}) {
  return useScopedMutation(scope, () => whatsappApi.syncTemplates(scope));
}

export function useWaDeleteTemplate(scope: WaScope = {}) {
  return useScopedMutation(scope, (id: string) => whatsappApi.deleteTemplate(id, scope));
}

type CreateCampaignVars = {
  body: CreateCampaignBody;
  /** When set, the new campaign is sent (or scheduled) in the same step. */
  send?: SendCampaignBody | null;
};

/** Create a campaign and, with `send`, hand it to the sender at once: two requests, one invalidation. */
export function useWaCreateCampaign(scope: WaScope = {}) {
  return useScopedMutation(scope, async ({ body, send }: CreateCampaignVars) => {
    const created = await whatsappApi.createCampaign(body, scope);
    return send ? whatsappApi.sendCampaign(created.id, send, scope) : created;
  });
}

/** Counts who a campaign would reach (POST, nothing sent): a dry run, so no invalidation. */
export function useWaPreviewCampaign(scope: WaScope = {}) {
  return useMutation({ mutationFn: (id: string) => whatsappApi.previewCampaign(id, scope) });
}

export function useWaSendCampaign(scope: WaScope = {}) {
  return useScopedMutation(scope, ({ id, body }: { id: string; body?: SendCampaignBody }) =>
    whatsappApi.sendCampaign(id, body, scope),
  );
}

export function useWaPauseCampaign(scope: WaScope = {}) {
  return useScopedMutation(scope, (id: string) => whatsappApi.pauseCampaign(id, scope));
}

export function useWaResumeCampaign(scope: WaScope = {}) {
  return useScopedMutation(scope, (id: string) => whatsappApi.resumeCampaign(id, scope));
}

export function useWaCancelCampaign(scope: WaScope = {}) {
  return useScopedMutation(scope, (id: string) => whatsappApi.cancelCampaign(id, scope));
}

export function useWaSendMessage(scope: WaScope = {}) {
  return useScopedMutation(scope, (body: SendMessageBody) => whatsappApi.sendMessage(body, scope));
}
